import { reloadData } from '@axo/form/data-access/backend-sync';
import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Icon } from '@axo/ui-core/components/Icon';
import { InfoCard } from '@axo/ui-feature/components/InfoCard/InfoCard';
import { ComponentProps, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ExtendedLoanOverview } from '../../../../../../components/ExtendedLoanOverview/ExtendedLoanOverview';
import { useAxoFormContext } from '../../../../../../context/AxoForm/AxoForm';
import type { NorwayFormData } from '../../../FormData.no.types';
import {
  DEFAULT_LOAN_AMOUNT,
  DEFAULT_LOAN_DURATION,
  MAX_LOAN_AMOUNT,
  MIN_LOAN_AMOUNT,
} from '../../../config/globals';
import { getAllDebts } from './utils/getAllDebts';

export const LoanOverview = () => {
  const { t } = useTranslation();

  const { applicationId } = reloadData.get();

  const { watch, setValue, register } = useFormContext();

  const { axoFormData } = useAxoFormContext<NorwayFormData>();

  const [debtRegistryDebts, debtRegistryTotal] = watch([
    'Frontend/DebtRegistryDebts',
    'Debt/Personal/OutstandingAmount',
  ]);

  const debtInfoProps = useMemo(
    () => ({
      debts: debtRegistryDebts,
      totalDebt: debtRegistryTotal,
      loansModalDebtEntriesTexts: t('LoanOverview.loansModalDebtEntriesTexts', {
        returnObjects: true,
      }) as any,
      loansModalTexts: t('LoanOverview.loansModalTexts', {
        returnObjects: true,
      }) as any,
    }),
    [debtRegistryDebts, debtRegistryTotal, t]
  );

  const extraProps = useMemo(
    (): ComponentProps<
      typeof ExtendedLoanOverview
    >['refinanceableDebtsExtraProps'] => ({
      isTotalAmountVisible: false,
      editType: 'toggle',
    }),
    []
  );

  if (!debtRegistryDebts) return null;

  const { debts: refinanceableDebts, allocationPriority } = getAllDebts({
    debts: debtRegistryDebts,
    labels: {
      interestBearing: t('LoanOverview.interestBearing'),
      nonInterestBearing: t('LoanOverview.nonInterestBearing'),
    },
    loanApplicationID: applicationId ?? '',
  });

  const noRefinanceableDebts = refinanceableDebts?.length === 0;

  const appliedAmount =
    axoFormData['LoanApplication/AppliedAmount'] ?? DEFAULT_LOAN_AMOUNT;
  const loanDuration =
    axoFormData['LoanApplication/LoanDuration'] ?? DEFAULT_LOAN_DURATION;

  const isRefinancing =
    axoFormData['LoanApplication/InitialPurpose'] === Purpose.RefinancingLoan;

  const labels = t('LoanOverview', { returnObjects: true }) as ComponentProps<
    typeof ExtendedLoanOverview
  >['labels'];

  const onRefinanceAmountChange: ComponentProps<
    typeof ExtendedLoanOverview
  >['onRefinanceAmountChange'] = ({ refinanceAmount: newValue }) => {
    if (!newValue || newValue < 0) {
      setValue('LoanApplication/Purpose', Purpose.Other, {
        shouldTouch: true,
        shouldDirty: true,
      });
      setValue('LoanApplication/TotalRefinancingAmount', 0, {
        shouldTouch: true,
        shouldDirty: true,
      });
      setValue('Debt/Personal/RefinanceAmount', 0, {
        shouldTouch: true,
        shouldDirty: true,
      });
      return;
    }

    setValue('LoanApplication/Purpose', Purpose.RefinancingLoan, {
      shouldTouch: true,
      shouldDirty: true,
    });
    setValue('LoanApplication/TotalRefinancingAmount', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
    setValue('Debt/Personal/RefinanceAmount', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const onTotalAmountChange = (newValue: number) => {
    if (axoFormData['LoanApplication/AppliedAmount'] === newValue) return;
    if (newValue < MIN_LOAN_AMOUNT || newValue > MAX_LOAN_AMOUNT) return;

    setValue('LoanApplication/AppliedAmount', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const onLoanDurationChange = (newValue: number) => {
    if (newValue < 0) return;
    if (axoFormData['LoanApplication/LoanDuration'] === newValue) return;
    setValue('LoanApplication/LoanDuration', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  return (
    <>
      <input
        type="hidden"
        {...register('LoanApplication/TotalRefinancingAmount', {
          valueAsNumber: true,
        })}
      />
      <input
        type="hidden"
        {...register('Debt/Personal/OutstandingAmount', {
          valueAsNumber: true,
        })}
      />
      <input
        type="hidden"
        {...register('Debt/Personal/RefinanceAmount', { valueAsNumber: true })}
      />

      <InfoCard
        title={t('SavingAmount.title', {
          context: noRefinanceableDebts ? 'empty' : undefined,
        })}
        description={t('SavingAmount.description', {
          context: noRefinanceableDebts ? 'empty' : undefined,
        })}
        icon={
          <Icon
            name={noRefinanceableDebts ? 'info-solid' : 'piggy-bank-solid'}
          />
        }
      />

      <ExtendedLoanOverview
        loanApplicationID={applicationId || undefined}
        labels={labels}
        currency="kr"
        appliedAmount={appliedAmount}
        loanDuration={loanDuration}
        maxLoanAmount={MAX_LOAN_AMOUNT}
        isUpSell={!isRefinancing}
        allocationPriority={allocationPriority}
        onTotalAmountChange={onTotalAmountChange}
        onLoanDurationChange={onLoanDurationChange}
        isDebtInfoButtonVisible
        debtInfoProps={debtInfoProps}
        isLoanDurationVisible={false}
        isEditButtonsVisible={false}
        initialEditField="cashAmount"
        refinanceableDebts={refinanceableDebts}
        refinanceableDebtsExtraProps={extraProps}
        onRefinanceAmountChange={onRefinanceAmountChange}
        // debounced change on page load doesn't trigger onRefinanceAmountChange
        debounceRefinanceAmountChange={false}
      />
    </>
  );
};
